import { useRef, useState } from 'react';
import { useDismissOnOutsideClick } from '../../hooks/useDismissOnOutsideClick';
import {
  useConversationTags,
  useTagConversation,
  useUntagConversation,
} from '../../hooks/useConversationTags';
import { useTagSuggestions } from '../../hooks/useTagForm/useTagForm';
import { notifySuccess } from '../../services/notify';

interface Props {
  projectId: string;
  collectionId: string;
  resourceId: string;
  chatMessageId: string;
  /**
   * Phone-image conversations untag in bulk across the whole conversation, which takes long
   * enough server-side that the removal needs announcing.
   */
  announceUntag?: boolean;
}

/**
 * Conversation-level tags in the conversation-view header (Angular resource.html ~724). Applies
 * / removes tags at the active conversation's chatMessageId, distinct from resource tagging.
 * Mirrors SimpleTagForm: a typeahead add-input plus removable tag pills. Rendered only when the
 * user has MANAGE_TAGS (gated by the caller).
 */
export default function ConversationTags({ projectId, collectionId, resourceId, chatMessageId, announceUntag }: Props) {
  const [tagInput, setTagInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const typeaheadRef = useRef<HTMLDivElement>(null);
  useDismissOnOutsideClick(typeaheadRef, showSuggestions, () => setShowSuggestions(false));

  const { data: tags = [] } = useConversationTags(projectId, collectionId, chatMessageId);
  const { data: suggestions = [] } = useTagSuggestions(projectId, tagInput, false);
  const tagConversation = useTagConversation(projectId, collectionId, chatMessageId);
  const untagConversation = useUntagConversation(projectId, collectionId, resourceId, chatMessageId);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const name = tagInput.trim();
    if (!name) return;
    tagConversation.mutate(name, { onSuccess: () => setTagInput('') });
  }

  return (
    <div className="iio-conv-tags" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }} data-testid="viewer-conversation-tags-panel">
      {[...tags]
        .sort((a, b) => a.categoryId.localeCompare(b.categoryId) || a.name.localeCompare(b.name))
        .map((tag) => (
          <div key={tag.id} className="tag" data-testid="viewer-conversation-tag-chip">
            <div className="btn-group">
              <button className="btn btn-sm btn-default">
                <i className="fas fa-circle" style={{ color: tag.color ?? undefined }} /> {tag.name}
              </button>
              {tag.isNameEditable && (
                <button
                  className="btn btn-default btn-sm"
                  title="Remove tag"
                  data-testid="viewer-conversation-tag-remove-btn"
                  disabled={untagConversation.isPending}
                  onClick={() => {
                    if (announceUntag) {
                      notifySuccess({
                        title: 'Untagging Started',
                        description: `Removing ${tag.name} from conversation.`,
                        timeout: 2500,
                      });
                    }
                    untagConversation.mutate(tag.id);
                  }}
                >
                  <i className="fas fa-times" />
                </button>
              )}
            </div>
          </div>
        ))}

      <form className="form-inline" onSubmit={handleSubmit} style={{ position: 'relative' }}>
        <div className="form-group" style={{ position: 'relative' }} ref={typeaheadRef}>
          <input
            type="text"
            className="form-control"
            placeholder="Tag conversation…"
            data-testid="viewer-conversation-tag-input"
            value={tagInput}
            onChange={(e) => { setTagInput(e.target.value); setShowSuggestions(true); }}
            onFocus={() => tagInput.length > 0 && setShowSuggestions(true)}
            disabled={tagConversation.isPending}
            autoComplete="off"
          />
          {showSuggestions && suggestions.length > 0 && (
            <ul className="dropdown-menu" style={{ display: 'block', width: '100%', maxHeight: 200, overflowY: 'auto' }} data-testid="viewer-conversation-tag-suggestion-list">
              {suggestions.map((s) => (
                <li key={s?.id}>
                  <a onMouseDown={() => { setTagInput(s?.name ?? ''); setShowSuggestions(false); }} data-testid="viewer-conversation-tag-suggestion-item">{s?.name}</a>
                </li>
              ))}
            </ul>
          )}
        </div>{' '}
        <button type="submit" className="btn btn-primary" disabled={tagConversation.isPending || !tagInput.trim()} data-testid="viewer-conversation-tag-add-btn">
          {tagConversation.isPending && <i className="fas fa-spinner fa-spin" />} Tag <i className="fas fa-plus-circle" />
        </button>
      </form>
    </div>
  );
}
